import React, { useEffect, useRef, useState } from 'react'
import {
  ArrowUp,
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  Flame,
  Loader2,
  Plus,
  Scissors,
  Sparkles,
} from 'lucide-react'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { toast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'
import {
  useAddProposedPiecesMutation,
  useAskContentAssistantMutation,
  useGetContentTrendsQuery,
  type AssistantMessage,
  type ProposedPiece,
} from '@/redux/collaborationsApi'
import { fmt } from './postHelpers'

type Turn = AssistantMessage & { pieces?: ProposedPiece[] }

const QUICK = [
  'Plan my next 2 weeks',
  'Ideas for game week',
  'Use what’s trending',
  'Fill my open days this week',
]
const platformLabel = (p: string) =>
  p === 'tiktok' ? 'TikTok' : p === 'youtube' ? 'YouTube' : 'Instagram'
const keyOf = (p: ProposedPiece) => `${p.postOn}|${p.title}`

const PieceCard: React.FC<{
  p: ProposedPiece
  added: boolean
  adding: boolean
  onAdd: () => void
}> = ({ p, added, adding, onAdd }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-2xl border border-border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-[#3F7A0A]">
            {p.themeLabel} · {platformLabel(p.platform)}
          </p>
          <p className="font-bold leading-snug text-foreground">{p.title}</p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={added || adding}
          className={cn(
            'inline-flex h-9 shrink-0 items-center gap-1 rounded-xl px-3 text-sm font-semibold',
            added
              ? 'bg-[#E8F9D2] text-[#2F5E06]'
              : 'bg-[#C0FD72] text-[#0F172A] hover:bg-[#B2F25E] disabled:opacity-50',
          )}
        >
          {added ? <Check className="h-4 w-4" strokeWidth={3} /> : <Plus className="h-4 w-4" />}
          {added ? 'Added' : 'Add'}
        </button>
      </div>
      <p className="mt-1.5 text-sm italic text-foreground">“{p.hook}”</p>
      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
        <span className="inline-flex items-center gap-1 rounded-full bg-[#CCFBF1] px-2 py-0.5 font-semibold text-[#0F766E]">
          <Camera className="h-3 w-3" /> Shoot{' '}
          {fmt(p.shootOn, { weekday: 'short', month: 'short', day: 'numeric' })}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#DBEAFE] px-2 py-0.5 font-semibold text-[#1D4ED8]">
          <Scissors className="h-3 w-3" /> Edit {fmt(p.editOn, { weekday: 'short' })}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#FCE7F3] px-2 py-0.5 font-semibold text-[#BE185D]">
          <CalendarDays className="h-3 w-3" /> Post{' '}
          {fmt(p.postOn, { weekday: 'short', month: 'short', day: 'numeric' })}
          {p.postTime ? ` · ${p.postTime}` : ''}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"
        aria-expanded={open}
      >
        {open ? 'Hide the plan' : 'See the plan'}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="mt-2 space-y-2 text-sm">
          <p className="text-foreground">{p.concept}</p>
          {p.whyNow ? <p className="text-muted-foreground">Why now: {p.whyNow}</p> : null}
          {p.shotList.length ? (
            <ol className="list-decimal space-y-0.5 pl-5 text-foreground">
              {p.shotList.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          ) : null}
          <p className="text-muted-foreground">
            {[p.location && `Where: ${p.location}`, p.wardrobe && `Wear: ${p.wardrobe}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {p.caption ? (
            <p className="rounded-lg bg-muted px-2.5 py-1.5 text-foreground">{p.caption}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Plan with AI: say what you want to post (or tap a starter) and get complete content pieces
 * back (the idea, a shot list, where to shoot, and shoot / edit / post days) to add to the
 * calendar in one tap. Works the same for a manager acting for the athlete.
 */
export const ContentAssistantSheet: React.FC<{
  open: boolean
  onOpenChange: (o: boolean) => void
  /** "your" for the athlete; the athlete's first name when a manager plans */
  whose?: string
  onAdded?: () => void
  /** A prompt to send as soon as the sheet opens (e.g. from a trend) */
  starter?: string | null
}> = ({ open, onOpenChange, whose, onAdded, starter }) => {
  const [turns, setTurns] = useState<Turn[]>([])
  const [text, setText] = useState('')
  const [added, setAdded] = useState<Set<string>>(new Set())
  const [ask, { isLoading: thinking }] = useAskContentAssistantMutation()
  const [addPieces, { isLoading: adding }] = useAddProposedPiecesMutation()
  const { data: trendData } = useGetContentTrendsQuery(undefined, { skip: !open })
  const trends = trendData?.trends ?? []
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns, thinking])

  const send = async (content: string) => {
    const message = content.trim()
    if (!message || thinking) return
    const next: Turn[] = [...turns, { role: 'user', content: message }]
    setTurns(next)
    setText('')
    try {
      const res = await ask({
        messages: next.map(({ role, content }) => ({ role, content })),
      }).unwrap()
      setTurns((t) => [...t, { role: 'assistant', content: res.reply, pieces: res.pieces }])
    } catch (err: any) {
      setTurns((t) => [
        ...t,
        {
          role: 'assistant',
          content: err?.data?.error ?? 'I couldn’t plan that just now. Try again?',
        },
      ])
    }
  }

  // A starter from outside (a trend tapped on the calendar)
  const lastStarter = useRef<string | null>(null)
  useEffect(() => {
    if (open && starter && starter !== lastStarter.current) {
      lastStarter.current = starter
      send(starter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, starter])

  const add = async (pieces: ProposedPiece[]) => {
    const fresh = pieces.filter((p) => !added.has(keyOf(p)))
    if (!fresh.length) return
    try {
      await addPieces({ pieces: fresh }).unwrap()
      setAdded((s) => new Set([...s, ...fresh.map(keyOf)]))
      toast(
        fresh.length === 1
          ? 'Added to the calendar'
          : `${fresh.length} posts added to the calendar`,
        { description: 'Shoot and edit days are on it too.' },
      )
      onAdded?.()
    } catch (err: any) {
      toast.error('Couldn’t add that', { description: err?.data?.error ?? err?.message })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mx-auto flex h-[92dvh] max-w-lg flex-col rounded-t-3xl p-0"
      >
        <div className="border-b border-border px-4 pb-3 pt-4">
          <SheetTitle className="flex items-center gap-2 text-lg font-bold text-foreground">
            <Sparkles className="h-5 w-5 text-[#3F7A0A]" /> Plan with AI
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Say what {whose ? `${whose} wants` : 'you want'} to post. I’ll plan it down to the
            shoot day.
          </SheetDescription>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {!turns.length ? (
            <>
              <div className="flex flex-wrap gap-2">
                {QUICK.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => send(q)}
                    className="rounded-full border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:border-[#84CC16]"
                  >
                    {q}
                  </button>
                ))}
              </div>
              {trends.length ? (
                <div className="rounded-2xl border border-[#FBCFE8] bg-[#FDF2F8] p-3">
                  <p className="flex items-center gap-1.5 text-sm font-bold text-foreground">
                    <Flame className="h-4 w-4 text-[#DB2777]" /> Trending on TikTok this week
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {trends.slice(0, 5).map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          onClick={() => send(`Give me an idea using ${t.title}`)}
                          className="w-full rounded-xl bg-card px-3 py-2 text-left text-sm hover:bg-muted"
                        >
                          <span className="font-semibold text-foreground">{t.title}</span>
                          {t.howToUse ? (
                            <span className="block text-xs text-muted-foreground">
                              {t.howToUse}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}

          {turns.map((t, i) =>
            t.role === 'user' ? (
              <p
                key={i}
                className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-[#0F172A] px-3 py-2 text-sm text-[#FFFFFF]"
              >
                {t.content}
              </p>
            ) : (
              <div key={i} className="space-y-2">
                <p className="w-fit max-w-[92%] rounded-2xl rounded-bl-md bg-muted px-3 py-2 text-sm text-foreground">
                  {t.content}
                </p>
                {t.pieces?.length ? (
                  <>
                    {t.pieces.map((p) => (
                      <PieceCard
                        key={keyOf(p)}
                        p={p}
                        added={added.has(keyOf(p))}
                        adding={adding}
                        onAdd={() => add([p])}
                      />
                    ))}
                    {t.pieces.length > 1 && t.pieces.some((p) => !added.has(keyOf(p))) ? (
                      <button
                        type="button"
                        onClick={() => add(t.pieces!)}
                        disabled={adding}
                        className="h-11 w-full rounded-xl bg-[#0F172A] text-sm font-semibold text-[#FFFFFF] disabled:opacity-50"
                      >
                        Add all {t.pieces.filter((p) => !added.has(keyOf(p))).length} to{' '}
                        {whose ? `${whose}'s` : 'my'} calendar
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>
            ),
          )}
          {thinking ? (
            <p className="inline-flex items-center gap-2 rounded-2xl bg-muted px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Planning…
            </p>
          ) : null}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            send(text)
          }}
          className="flex items-end gap-2 border-t border-border px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3"
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                send(text)
              }
            }}
            rows={1}
            placeholder="e.g. 3 posts a week in November, lean into game day"
            aria-label="Message the planner"
            className="max-h-32 min-h-11 flex-1 resize-none rounded-xl border border-border bg-card px-3.5 py-2.5 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40"
          />
          <button
            type="submit"
            aria-label="Send"
            disabled={!text.trim() || thinking}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#C0FD72] text-[#0F172A] disabled:opacity-40"
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export default ContentAssistantSheet
